import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'found';
import { Box, Button, IconButton, SxProps, Theme } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import CloseIcon from '../../assets/close.svg';
import { BannerBar, BannerBarMessage } from '../Banner/BannerBar';
import useScreenSize from '../../utils/useScreenSize';

export interface PromoBannerProps {
  headline: string;
  body: string;
  ctaLabel: string;
  /** Internal `found` route for the CTA. Omit to render the CTA as a plain button driven by `onCtaClick`. */
  ctaHref?: string;
  /** Fires on CTA click (in addition to navigating, when `ctaHref` is set). */
  onCtaClick?: () => void;
  /** Optional secondary (outlined) CTA. Rendered only when a label is provided. */
  secondaryLabel?: string;
  /** Internal `found` route for the secondary CTA. Omit to render it as a plain button driven by `onSecondaryClick`. */
  secondaryHref?: string;
  /** Fires on secondary CTA click (in addition to navigating, when `secondaryHref` is set). */
  onSecondaryClick?: () => void;
  /** Leading icon. Defaults to an info icon. */
  icon?: ReactNode;
  /** When provided, renders a dismiss button that calls this. Omit for a non-dismissible banner. */
  onDismiss?: () => void;
  sx?: SxProps<Theme>;
}

// Shape and padding come from the theme's `sizeSmall` overrides; only the
// white-on-banner colors are local (the contained variant's primary background
// and hover shade don't work on the blue bar).
const getCtaStyles = (isMobile: boolean): SxProps<Theme> => ({
  flexShrink: 0,
  backgroundColor: palette.common.white,
  color: palette.common.black,
  '&:hover': {
    backgroundColor: palette.common.white,
    color: palette.common.black,
  },
  width: isMobile ? '100%' : 'auto',
});

const getSecondaryCtaStyles = (isMobile: boolean): SxProps<Theme> => ({
  flexShrink: 0,
  borderColor: palette.common.white,
  color: palette.common.white,
  '&:hover': {
    borderColor: palette.common.white,
    color: palette.common.white,
  },
  width: isMobile ? '100%' : 'auto',
});

const PromoBanner: React.FC<PromoBannerProps> = ({
  headline,
  body,
  ctaLabel,
  ctaHref,
  onCtaClick,
  secondaryLabel,
  secondaryHref,
  onSecondaryClick,
  icon,
  onDismiss,
  sx,
}) => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isMobile = isXsScreen || isSmScreen;

  const ctaProps = ctaHref ? { component: Link, to: ctaHref } : {};
  const secondaryProps = secondaryHref ? { component: Link, to: secondaryHref } : {};

  return (
    <BannerBar
      backgroundColor={palette.info.main}
      color={palette.common.white}
      sx={[
        {
          borderRadius: borderRadius.xs,
          textAlign: 'left',
          position: 'relative',
          ...(isMobile ? {} : { alignItems: 'flex-start' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <BannerBarMessage>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: spaces.sm,
          // Clear the absolute dismiss button (top-right) on mobile so a long
          // headline never runs under it; desktop keeps the × in flow.
          paddingRight: onDismiss && isMobile ? spaces.xl : 0,
        }}>
          {icon || <InfoOutlined sx={{ flexShrink: 0 }} />}
          <Box>
            {/* `color: inherit` after `typography` keeps the banner's own text color
                (the variants bake in `text.secondary`, unreadable on the banner). */}
            <Box sx={{ typography: 'body1', color: 'inherit' }}>{headline}</Box>
            <Box sx={{ typography: 'body2', color: 'inherit', opacity: 0.9 }}>{body}</Box>
          </Box>
        </Box>
      </BannerBarMessage>
      <Box sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: 0,
        flexShrink: 0,
        gap: spaces.xs,
        marginTop: isMobile ? spaces.xs : 0,
        marginLeft: isMobile ? 0 : spaces.md,
        paddingBottom: isMobile ? spaces.xs : 0,
        justifyContent: isMobile ? 'flex-start' : 'flex-end',
        width: isMobile ? '100%' : 'auto',
      }}>
        <Button
          size='small'
          variant='contained'
          disableElevation
          {...ctaProps}
          onClick={onCtaClick}
          sx={getCtaStyles(isMobile)}
        >
          {ctaLabel}
        </Button>
        {secondaryLabel && (
          <Button
            size='small'
            variant='outlined'
            {...secondaryProps}
            onClick={onSecondaryClick}
            sx={getSecondaryCtaStyles(isMobile)}
          >
            {secondaryLabel}
          </Button>
        )}
        {onDismiss && (
          <IconButton
            data-testid='dismiss-promo-banner'
            color='inherit'
            aria-label={t('Close')}
            onClick={onDismiss}
            sx={isMobile
              ? { position: 'absolute', top: spaces.xxs, right: spaces.xxs }
              : { position: 'static' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </BannerBar>
  );
};

export default PromoBanner;
